import { Head } from "@inertiajs/react"
import { Check } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { TravelTrackerSubNav } from "@/components/travel_tracker/TravelTrackerSubNav"
import { WeekStrip } from "@/components/travel_tracker/WeekStrip"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type {
  LocationPerson,
  SummaryDay,
  SummaryLocation,
  SummaryPaging,
  WeekOption,
} from "@/types/travel_tracker"

const DESCRIPTION =
  "The same week read by place rather than by person — every location somebody worked from, with everyone who was there listed underneath."

export default function TravelTrackerLocationSummary({
  week,
  weeks,
  days,
  locations,
  paging,
}: {
  week: WeekOption
  weeks: WeekOption[]
  days: SummaryDay[]
  locations: SummaryLocation[]
  paging: SummaryPaging
}) {
  // The person column, the seven days, and the trailing Days total.
  const columns = days.length + 2
  const totalDays = locations.reduce((sum, location) => sum + location.recorded, 0)

  return (
    <>
      <Head title="Travel Location Summary">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Travel Location Summary" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="Travel Tracker"
          description="Who was where, a week at a time."
          tabs={<TravelTrackerSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          <WeekStrip week={week} weeks={weeks} paging={paging} path="/travel_tracker/locations" />

          <p className="mt-4 text-sm text-ink-muted">
            {locations.length === 0
              ? `Nobody recorded travel in ${week.label}.`
              : `${totalDays} ${totalDays === 1 ? "day" : "days"} across ${locations.length} ${
                  locations.length === 1 ? "place" : "places"
                } in ${week.label}.`}
          </p>

          {locations.length > 0 && (
            <div className="mt-4">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell sticky>Person</TableHeaderCell>
                    {days.map((day) => (
                      <TableHeaderCell key={day.date} className="text-center">
                        <span className="block">{day.day_name}</span>
                        <span className="block font-normal text-ink-muted">
                          {day.month_label} {day.day_label}
                        </span>
                      </TableHeaderCell>
                    ))}
                    <TableHeaderCell numeric>Days</TableHeaderCell>
                  </TableRow>
                </TableHead>

                {locations.map((location) => (
                  /* One tbody per location, for the same divider reason the
                     people summary has one per department. */
                  <TableBody key={location.id}>
                    <TableRow className="bg-surface">
                      {/* Sticky span inside the wide cell, never a sticky cell —
                          see the note on the people summary. */}
                      <TableCell colSpan={columns} className="py-3">
                        <span className="sticky left-3 inline-block">
                          <span className="text-sm font-semibold text-ink-display">
                            {location.code}
                          </span>
                          <span className="ml-2 text-ink-muted">
                            {location.recorded} {location.recorded === 1 ? "day" : "days"} across{" "}
                            {location.people.length}{" "}
                            {location.people.length === 1 ? "person" : "people"}
                          </span>
                        </span>
                      </TableCell>
                    </TableRow>
                    {location.people.map((person) => (
                      <PersonRow
                        key={person.id}
                        person={person}
                        days={days}
                        code={location.code}
                      />
                    ))}
                  </TableBody>
                ))}
              </Table>
            </div>
          )}

          <p className="mt-4 text-xs text-ink-muted">
            Only places somebody actually worked from that week appear. Somebody who was in two places
            over the week is listed under both.
          </p>
        </div>
      </AppShell>
    </>
  )
}

function PersonRow({
  person,
  days,
  code,
}: {
  person: LocationPerson
  days: SummaryDay[]
  code: string
}) {
  return (
    <TableRow>
      <TableCell sticky nowrap>
        {person.name}
      </TableCell>
      {person.cells.map((there, index) => (
        <TableCell key={days[index].date} className="text-center">
          {there ? (
            <>
              <Check className="inline-block size-4 text-accent" aria-hidden="true" />
              <span className="sr-only">
                {person.name} was at {code} on {days[index].day_name} {days[index].month_label}{" "}
                {days[index].day_label}
              </span>
            </>
          ) : (
            <span className="text-ink-muted">
              <span aria-hidden="true">&mdash;</span>
              <span className="sr-only">
                {person.name} was not at {code} on {days[index].day_name}{" "}
                {days[index].month_label} {days[index].day_label}
              </span>
            </span>
          )}
        </TableCell>
      ))}
      <TableCell numeric>{person.recorded}</TableCell>
    </TableRow>
  )
}
